import React, { FC, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Utils from '../utils';
import BundleCover from './BundleCover';
import BundleBadge from './BundleBadge';
import {
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse,
    BundleOut
} from '../../store/api';
import { BundleTileBottom } from './BundleTileBottom';
import { PriceTier } from '../../../models/PriceTier';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { useTranslation } from 'react-i18next';
import { SubscriptionPeriodType } from '../../../models/Article';
import { Orderlist } from '../../../models/OrderList';

type Props = {
    readonly bundle: BundleOut;
    readonly onAddToCart: (
        bundleId: string,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onAddToList: (
        bundleId: string,
        list: Orderlist,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
};

const BundleTile: FC<Props> = ({ bundle, onAddToCart, onAddToList }) => {
    const { t } = useTranslation();
    const [isShowDetails, setIsShowDetails] = useState(false);
    const [isShowViewDetailsLabel, setIsShowViewDetailsLabel] = useState(false);

    const showDetails = useCallback(() => setIsShowDetails(true), []);
    const hideDetails = useCallback(() => setIsShowDetails(false), []);

    const showViewDetailsLabel = useCallback(() => setIsShowViewDetailsLabel(true), []);
    const hideViewDetailsLabel = useCallback(() => setIsShowViewDetailsLabel(false), []);

    const hideElements = useCallback(() => {
        hideDetails();
        hideViewDetailsLabel();
    }, [hideDetails, hideViewDetailsLabel]);

    const showElements = useCallback(() => {
        showDetails();
        showViewDetailsLabel();
    }, [showDetails, showViewDetailsLabel]);

    const loanFormat = mapLoanFormat(bundle.loanFormat)!;

    // default information
    const shortenedTitle = Utils.truncate(bundle.title);

    // dropdown and hover state
    const expandableClasses = classNames('bndl-expandable', isShowDetails && 'over');
    const imageOverlayClasses = classNames('bndl-img-overlay', isShowViewDetailsLabel && 'over');

    const bundleTileBottomProps = {
        bundle,
        onAddToList,
        onAddToCart,
        loanFormat
    };

    return (
        <div className="bndl-tile" onMouseLeave={hideElements}>
            <div className="bndl-tile-inner">
                <Link to={`/bundles/${bundle.bundleId}`}>
                    <div className="bndl-cover" onMouseEnter={showElements}>
                        <div className={imageOverlayClasses}>
                            <span>{t('shop.orders.viewDetails')}</span>
                            <i className="material-icons md-upper cold-bgreen">arrow_forward</i>
                        </div>
                        <BundleCover bundle={bundle} />
                    </div>
                </Link>

                <div className="details">
                    {bundle.salesTag && <BundleBadge bundle={bundle} />}
                    <div className="bndl-title">{shortenedTitle}</div>
                </div>
                <div className={expandableClasses}>
                    <div className="bndl-description" onMouseEnter={showElements}>
                        {bundle.description}
                    </div>
                    <BundleTileBottom {...bundleTileBottomProps} />
                </div>
            </div>
        </div>
    );
};

export default BundleTile;
